import React, { useState, useEffect } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  CreditCard,
  AlertCircle,
  CheckCircle,
  Loader,
  ExternalLink,
  Crown,
  Users,
  Building2,
  FileText,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import { useAuth } from '../../hooks/useAuth';

interface OutletContextType {
  userData: any;
  companyData: any;
}

// Tier definitions matching shared-schemas
const TIER_DEFINITIONS = {
  plus: {
    name: 'plus',
    displayName: 'VALUTTEN Plus',
    limits: { fileTemplates: 1, offices: 1, users: 5 },
    pricing: { monthly: 49700, annual: 497000, setupFee: 99700 },
    features: [
      '1 aggregator template',
      '1 office',
      'Up to 5 users',
      'Commission tracking dashboard',
      'Basic reporting',
      'Clawback report',
      '48-hour support SLA'
    ]
  },
  pro: {
    name: 'pro',
    displayName: 'VALUTTEN Pro',
    limits: { fileTemplates: 3, offices: 5, users: 30 },
    pricing: { monthly: 79700, annual: 797000, setupFee: 149700 },
    features: [
      'Up to 3 aggregator templates',
      'Up to 5 offices',
      'Up to 30 users',
      'Office dashboard',
      'Multi-entity upload',
      'Trail book valuation',
      '24-hour support SLA'
    ]
  },
  enterprise: {
    name: 'enterprise',
    displayName: 'VALUTTEN Enterprise',
    limits: { fileTemplates: -1, offices: -1, users: -1 },
    pricing: { monthly: 199700, annual: 1997000, setupFee: 249700 },
    features: [
      'Unlimited templates',
      'Unlimited offices',
      'Unlimited users',
      'Dedicated account manager',
      'API access',
      'Custom reporting',
      '8-hour support SLA'
    ]
  }
};

type TierKey = keyof typeof TIER_DEFINITIONS;

// Helper to format cents to dollars
const formatCurrency = (cents: number): string => {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(cents / 100);
};

// Helper to get tier badge color
const getTierBadgeStyle = (tier: string) => {
  switch (tier?.toLowerCase()) {
    case 'plus':
      return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    case 'pro':
      return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    case 'enterprise':
      return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    default:
      return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  }
};

// Usage meter component
const UsageMeter: React.FC<{
  label: string;
  used: number;
  limit: number;
  icon: React.ReactNode;
}> = ({ label, used, limit, icon }) => {
  const isUnlimited = limit === -1;
  const percentage = isUnlimited ? 0 : Math.min((used / limit) * 100, 100);
  const isNearLimit = !isUnlimited && percentage >= 80;
  const isAtLimit = !isUnlimited && percentage >= 100;

  return (
    <div className="bg-gray-700/30 p-4 rounded-lg border border-gray-600">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-gray-300">
          {icon}
          <span className="text-sm font-medium">{label}</span>
        </div>
        <span className={`text-sm font-semibold ${
          isAtLimit ? 'text-red-400' : isNearLimit ? 'text-yellow-400' : 'text-white'
        }`}>
          {used} / {isUnlimited ? 'Unlimited' : limit}
        </span>
      </div>
      {!isUnlimited && (
        <div className="w-full bg-gray-600 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all ${
              isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-yellow-500' : 'bg-cyan-500'
            }`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      )}
      {isUnlimited && (
        <div className="w-full bg-gradient-to-r from-amber-500/30 to-amber-600/30 rounded-full h-2" />
      )}
    </div>
  );
};

// Plan comparison card
const PlanCard: React.FC<{
  tier: TierKey;
  isCurrentPlan: boolean;
  onUpgrade?: () => void;
}> = ({ tier, isCurrentPlan, onUpgrade }) => {
  const plan = TIER_DEFINITIONS[tier];
  const isEnterprise = tier === 'enterprise';

  return (
    <div className={`relative bg-gray-800/50 rounded-xl border ${
      isCurrentPlan
        ? 'border-cyan-500 ring-2 ring-cyan-500/20'
        : isEnterprise
          ? 'border-amber-500/50'
          : 'border-gray-700'
    } p-6 flex flex-col`}>
      {isCurrentPlan && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span className="bg-cyan-500 text-white text-xs font-bold px-3 py-1 rounded-full">
            Current Plan
          </span>
        </div>
      )}
      {isEnterprise && !isCurrentPlan && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
            <Crown size={12} /> Best Value
          </span>
        </div>
      )}

      <div className="mb-4">
        <h3 className={`text-xl font-bold ${
          isEnterprise ? 'text-amber-400' : tier === 'pro' ? 'text-purple-400' : 'text-blue-400'
        }`}>
          {plan.displayName}
        </h3>
        <div className="mt-2">
          <span className="text-3xl font-bold text-white">
            {formatCurrency(plan.pricing.monthly)}
          </span>
          <span className="text-gray-400">/mo</span>
        </div>
        <p className="text-sm text-gray-400 mt-1">
          or {formatCurrency(plan.pricing.annual)}/year (save ~17%)
        </p>
      </div>

      <div className="border-t border-gray-700 pt-4 mb-4 flex-grow">
        <p className="text-sm text-gray-400 mb-3">Includes:</p>
        <ul className="space-y-2">
          {plan.features.map((feature, idx) => (
            <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
              <CheckCircle size={16} className={`mt-0.5 flex-shrink-0 ${
                isEnterprise ? 'text-amber-400' : tier === 'pro' ? 'text-purple-400' : 'text-blue-400'
              }`} />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      {!isCurrentPlan && onUpgrade && (
        <button
          onClick={onUpgrade}
          className={`w-full py-3 px-4 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all ${
            isEnterprise
              ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white'
              : 'bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-700 hover:to-cyan-700 text-white'
          }`}
        >
          Upgrade <ArrowRight size={16} />
        </button>
      )}
      {isCurrentPlan && (
        <div className="w-full py-3 px-4 rounded-lg bg-gray-700/50 text-gray-400 text-center font-medium">
          Your Current Plan
        </div>
      )}
    </div>
  );
};

const BillingPage: React.FC = () => {
  const { userData, companyData } = useOutletContext<OutletContextType>();
  const { claims } = useAuth();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Real-time usage data from backend
  const [usage, setUsage] = useState({
    templates: 0,
    offices: 0,
    users: 0
  });

  // Extract subscription data (fallback to company doc)
  const subscription = companyData?.subscription;
  const currentTier = (subscription?.tier || 'plus').toLowerCase() as TierKey;
  const tierDef = TIER_DEFINITIONS[currentTier] || TIER_DEFINITIONS.plus;
  const billingPeriod = subscription?.billing?.period || 'monthly';
  const isAnnual = billingPeriod === 'annual';

  // Fetch real-time usage from backend
  useEffect(() => {
    const fetchUsage = async () => {
      try {
        const functions = getFunctions(undefined, 'australia-southeast1');
        const getStatus = httpsCallable(functions, 'getSubscriptionStatus');
        const result = await getStatus({});
        const data = result.data as any;

        if (data.usage) {
          setUsage({
            templates: data.usage.fileTemplates?.current || 0,
            offices: data.usage.offices?.current || 0,
            users: data.usage.users?.current || 0
          });
        }
      } catch (err) {
        console.error('Failed to fetch usage:', err);
        // Fallback to company doc data
        setUsage({
          templates: companyData?.templateCount || 1,
          offices: companyData?.officeCount || 1,
          users: companyData?.userCount || 1
        });
      } finally {
        setLoadingStatus(false);
      }
    };

    fetchUsage();
  }, [companyData]);

  // Check for success parameter in URL (return from Stripe)
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      setSuccessMessage('Payment method updated successfully!');
      setTimeout(() => setSuccessMessage(null), 5000);
    }
  }, [searchParams]);

  const handleManagePayment = async () => {
    setError(null);
    setLoading(true);

    try {
      const functions = getFunctions(undefined, 'australia-southeast1');
      const createPortalSession = httpsCallable(functions, 'createCustomerPortalSession');

      const result = await createPortalSession({
        companyId: companyData?.id || claims?.companyId
      });

      const data = result.data as { url: string };

      if (!data.url) {
        throw new Error('No portal URL returned from server');
      }

      // Open in new tab so user doesn't lose their place
      window.open(data.url, '_blank');
      setLoading(false);
    } catch (err: any) {
      setLoading(false);

      if (err.code === 'permission-denied') {
        setError('You do not have permission to manage billing. Please contact your administrator.');
      } else if (err.message?.includes('no subscription')) {
        setError('No active subscription found. Please contact support to set up billing.');
      } else if (err.message?.includes('no customer')) {
        setError('Billing account not found. Please contact support@valutten.com to set up billing.');
      } else {
        setError(err.message || 'Failed to open billing portal. Please try again or contact support.');
      }
    }
  };

  const handleUpgrade = () => {
    // Open Stripe portal for upgrade
    handleManagePayment();
  };

  // Check if user has permission to manage billing
  const canManageBilling = (userData?.role || claims?.role) === 'super_admin';

  if (!canManageBilling) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="max-w-md w-full bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-yellow-500/30">
          <div className="text-center">
            <div className="flex items-center justify-center text-yellow-400 mb-4">
              <AlertCircle className="mr-2" size={24} />
              <h3 className="text-xl font-semibold">Access Restricted</h3>
            </div>
            <p className="text-gray-300">
              You do not have permission to manage billing settings.
              Please contact your administrator if you need access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <PageHeader
          icon={CreditCard}
          title="Billing & Subscription"
          subtitle="Manage your subscription, usage, and payment methods"
        />

        {/* Success Message */}
        {successMessage && (
          <div className="bg-green-900/50 border border-green-500 text-green-200 p-4 rounded-lg mb-6 flex items-center">
            <CheckCircle className="mr-2" size={20} />
            {successMessage}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-900/50 border border-red-500 text-red-200 p-4 rounded-lg mb-6 flex items-center">
            <AlertCircle className="mr-2" size={20} />
            {error}
          </div>
        )}

        {/* Current Plan Card */}
        <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-gray-700 mb-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-indigo-500 to-cyan-500 flex items-center justify-center mr-4">
                <CreditCard size={24} />
              </div>
              <div>
                <h3 className="text-2xl font-bold">Your Current Plan</h3>
                <p className="text-gray-400">Manage your subscription and billing</p>
              </div>
            </div>
            <div className={`px-4 py-2 rounded-full border text-sm font-semibold ${getTierBadgeStyle(currentTier)}`}>
              {tierDef.displayName}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Plan & Price */}
            <div className="bg-gray-700/30 p-6 rounded-lg border border-gray-600">
              <p className="text-sm text-gray-400 mb-2">Current Plan</p>
              <p className="text-2xl font-bold text-white mb-1">{tierDef.displayName}</p>
              <p className="text-lg text-cyan-400">
                {formatCurrency(isAnnual ? tierDef.pricing.annual / 12 : tierDef.pricing.monthly)}/mo
              </p>
              {isAnnual && (
                <span className="inline-block mt-2 px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                  Annual billing (17% savings)
                </span>
              )}
            </div>

            {/* Billing Status */}
            <div className="bg-gray-700/30 p-6 rounded-lg border border-gray-600">
              <p className="text-sm text-gray-400 mb-2">Status</p>
              {subscription?.state && (
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                  subscription.state === 'active'
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                    : subscription.state === 'trialing'
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : subscription.state === 'grace_period'
                    ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                    : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                }`}>
                  {subscription.state.charAt(0).toUpperCase() + subscription.state.slice(1).replace('_', ' ')}
                </span>
              )}
              {!subscription?.state && (
                <span className="inline-block px-3 py-1 rounded-full text-sm font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                  Active
                </span>
              )}
            </div>

            {/* Next Billing Date */}
            <div className="bg-gray-700/30 p-6 rounded-lg border border-gray-600">
              <p className="text-sm text-gray-400 mb-2">Next Billing Date</p>
              <p className="text-2xl font-bold text-white">
                {subscription?.billing?.nextBillingDate
                  ? new Date(
                      typeof subscription.billing.nextBillingDate.seconds === 'number'
                        ? subscription.billing.nextBillingDate.seconds * 1000
                        : subscription.billing.nextBillingDate
                    ).toLocaleDateString('en-AU', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })
                  : 'N/A'
                }
              </p>
            </div>
          </div>

          {/* Usage Meters */}
          <div className="mb-8">
            <h4 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Sparkles size={20} className="text-cyan-400" />
              Plan Usage
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <UsageMeter
                label="Loan Books"
                used={usage.templates}
                limit={tierDef.limits.fileTemplates}
                icon={<FileText size={16} />}
              />
              <UsageMeter
                label="Offices"
                used={usage.offices}
                limit={tierDef.limits.offices}
                icon={<Building2 size={16} />}
              />
              <UsageMeter
                label="Users"
                used={usage.users}
                limit={tierDef.limits.users}
                icon={<Users size={16} />}
              />
            </div>
          </div>

          {/* Manage Payment Button */}
          <button
            onClick={handleManagePayment}
            disabled={loading}
            className="w-full bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-700 hover:to-cyan-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
          >
            {loading ? (
              <>
                <Loader className="animate-spin mr-2" size={20} />
                Opening Billing Portal...
              </>
            ) : (
              <>
                <CreditCard className="mr-2" size={20} />
                Manage Payment Methods
                <ExternalLink className="ml-2" size={16} />
              </>
            )}
          </button>
          <p className="text-sm text-gray-400 mt-4 text-center">
            Securely manage your payment methods, view invoices, and update subscription through Stripe.
          </p>
        </div>

        {/* Plan Comparison Section */}
        {currentTier !== 'enterprise' && (
          <div className="bg-gray-800/50 backdrop-blur-sm p-8 rounded-2xl shadow-xl border border-gray-700 mb-6">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold mb-2">Compare Plans</h3>
              <p className="text-gray-400">Upgrade to unlock more features and capacity</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <PlanCard
                tier="plus"
                isCurrentPlan={currentTier === 'plus'}
                onUpgrade={currentTier !== 'plus' ? undefined : handleUpgrade}
              />
              <PlanCard
                tier="pro"
                isCurrentPlan={currentTier === 'pro'}
                onUpgrade={currentTier === 'plus' ? handleUpgrade : undefined}
              />
              <PlanCard
                tier="enterprise"
                isCurrentPlan={false}
                onUpgrade={handleUpgrade}
              />
            </div>
          </div>
        )}

        {/* About Billing Portal */}
        <div className="bg-gray-800/30 backdrop-blur-sm p-6 rounded-xl border border-gray-700">
          <h4 className="text-lg font-semibold mb-4 text-white">About Billing Portal</h4>
          <p className="text-gray-300 mb-4">The billing portal allows you to:</p>
          <ul className="space-y-2 text-gray-300">
            <li className="flex items-start">
              <CheckCircle className="mr-2 mt-1 flex-shrink-0 text-green-400" size={16} />
              <span>Update payment methods and billing information</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="mr-2 mt-1 flex-shrink-0 text-green-400" size={16} />
              <span>View and download invoices and payment history</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="mr-2 mt-1 flex-shrink-0 text-green-400" size={16} />
              <span>Manage your subscription plan and billing cycle</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="mr-2 mt-1 flex-shrink-0 text-green-400" size={16} />
              <span>Cancel or modify your subscription</span>
            </li>
          </ul>
          <p className="text-sm text-gray-400 mt-4">
            Need help? Contact our support team at <a href="mailto:support@valutten.com" className="text-indigo-400 hover:text-indigo-300">support@valutten.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default BillingPage;
